<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Download, Library, Search, Share2, SlidersHorizontal } from "@lucide/vue";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type { Track } from "@/types";

const tracks = ref<Track[]>([]);
const query = ref("");
const status = ref("all");
const loading = ref(true);

const filtered = computed(() =>
  tracks.value.filter((track) => {
    const matchQuery = `${track.title} ${track.genre}`.toLowerCase().includes(query.value.toLowerCase());
    const matchStatus = status.value === "all" || track.status === status.value;
    return matchQuery && matchStatus;
  }),
);

onMounted(async () => {
  try {
    tracks.value = (await api.tracks()).tracks;
  } finally {
    loading.value = false;
  }
});

async function share(track: Track) {
  const url = `${location.origin}/listen/${track.id}`;
  await navigator.clipboard.writeText(url);
  alert("公开试听链接已复制");
}
</script>

<template>
  <div class="page-stack">
    <section class="catalog-toolbar panel">
      <label class="search-field"><Search :size="17" /><input v-model="query" placeholder="搜索歌名或风格" /></label>
      <div class="filter-tabs">
        <SlidersHorizontal :size="16" />
        <button :class="{ active: status === 'all' }" @click="status = 'all'">全部</button>
        <button :class="{ active: status === 'ready' }" @click="status = 'ready'">待发行</button>
        <button :class="{ active: status === 'released' }" @click="status = 'released'">已发行</button>
      </div>
      <span class="catalog-count">{{ filtered.length }} 首作品</span>
    </section>

    <section v-if="filtered.length" class="catalog-grid">
      <article v-for="(track, index) in filtered" :key="track.id" class="catalog-card panel">
        <TrackArtwork :title="track.title" :index="index" />
        <div class="catalog-card-body">
          <div class="catalog-title">
            <div><h3>{{ track.title }}</h3><p>{{ track.artist }} · {{ track.genre }}</p></div>
            <span class="status-tag">{{ track.status === "released" ? "已发行" : track.status === "scheduled" ? "已排期" : "母带就绪" }}</span>
          </div>
          <audio :src="track.audioUrl" controls preload="metadata" />
          <div class="track-meta">
            <span>{{ Math.ceil(track.durationMs / 1000) || "—" }} 秒</span>
            <span>{{ (track.size / 1024 / 1024).toFixed(1) }} MB</span>
            <span>{{ track.plays }} 次试听</span>
          </div>
          <div class="card-actions">
            <a class="button button-ghost button-sm" :href="track.audioUrl" :download="`${track.title}.${track.audioFormat}`"><Download :size="15" />{{ track.audioFormat.toUpperCase() }}</a>
            <button class="button button-ghost button-sm" @click="share(track)"><Share2 :size="15" />分享</button>
            <router-link class="button button-dark button-sm" :to="`/release?track=${track.id}`">去发行</router-link>
          </div>
        </div>
      </article>
    </section>

    <section v-else-if="!loading" class="empty-catalog panel">
      <span><Library :size="32" /></span>
      <h2>{{ query ? "没有匹配的作品" : "你的第一张母带在等你" }}</h2>
      <p>{{ query ? "换一个关键词或筛选条件试试。" : "完成音乐生成后，高音质 WAV 母带会安全保存并出现在这里。" }}</p>
      <router-link v-if="!query" class="button button-lime" to="/create">开始创作</router-link>
    </section>
  </div>
</template>
